import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Skeleton } from "./Skeleton";

interface PointsChartProps {
  walletAddress: string;
  /** Lifetime points spent, from the wallet's points summary (for the per-pack estimate). */
  pointsUsed?: number | null;
  /** Free packs the wallet has redeemed, from imported spin history (for the per-pack estimate). */
  freePacksRedeemed?: number | null;
}

type TrendWindow = 7 | 30;

const DAY = 86_400_000;
const DEFAULT_POINTS_PER_PACK = 100_000;
const PACK_COST_KEY = "cc:points-per-pack";

// Points quarters start in Dec, Mar, Jun and Sep (see QuarterSelector)
const QUARTER_START_MONTHS = [2, 5, 8, 11]; // 0-based: Mar, Jun, Sep, Dec

/** Start of the next points quarter (UTC), i.e. when the current one ends. */
const nextQuarterStart = (from: Date): Date => {
  const year = from.getUTCFullYear();
  const candidates = QUARTER_START_MONTHS.map((m) => Date.UTC(year, m, 1));
  candidates.push(Date.UTC(year + 1, 2, 1)); // Mar next year, in case we're past Dec 1
  const next = candidates.find((t) => t > from.getTime());
  return new Date(next ?? Date.UTC(year + 1, 2, 1));
};

const dateShort = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const dateLong = (t: number) =>
  new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const compact = (v: number) =>
  Math.abs(v) >= 1_000_000
    ? `${(v / 1_000_000).toFixed(1)}M`
    : Math.abs(v) >= 1000
      ? `${Math.round(v / 1000)}k`
      : `${Math.round(v)}`;

const readPackCost = (): number => {
  try {
    const v = Number(localStorage.getItem(PACK_COST_KEY));
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_POINTS_PER_PACK;
  } catch {
    return DEFAULT_POINTS_PER_PACK;
  }
};

/**
 * Least-squares slope (points per day) over the snapshots inside `windowMs`
 * before the last one. Needs at least two snapshots at least six hours apart.
 */
function fitVelocity(
  points: { t: number; total: number }[],
  windowMs: number
): { perDay: number; samples: number } | null {
  const last = points[points.length - 1];
  if (!last) return null;
  const window = points.filter((p) => p.t >= last.t - windowMs);
  const first = window[0];
  if (!first || window.length < 2 || last.t - first.t < 6 * 3_600_000) return null;
  const n = window.length;
  const meanT = window.reduce((s, p) => s + p.t, 0) / n;
  const meanY = window.reduce((s, p) => s + p.total, 0) / n;
  let num = 0, den = 0;
  for (const p of window) {
    num += (p.t - meanT) * (p.total - meanY);
    den += (p.t - meanT) ** 2;
  }
  if (den === 0) return null;
  return { perDay: (num / den) * DAY, samples: n };
}

export function PointsChart({ walletAddress, pointsUsed, freePacksRedeemed }: PointsChartProps) {
  const [trendWindow, setTrendWindow] = useState<TrendWindow>(7);
  const [packCost, setPackCost] = useState<number>(readPackCost);
  const updatePackCost = (value: number) => {
    setPackCost(value);
    try {
      localStorage.setItem(PACK_COST_KEY, String(value));
    } catch {
      /* fine */
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["points-history", walletAddress],
    queryFn: async () => {
      const { data, error } = await api
        .wallet({ walletAddress })
        .pointsHistory.get({ query: { limit: 1000 } });
      if (error) throw error;
      return data;
    },
    enabled: !!walletAddress,
  });

  const model = useMemo(() => {
    const rows = (data?.data ?? [])
      .map((p) => ({
        t: new Date(p.recordedAt).getTime(),
        total: p.totalPoints ?? 0,
        primary: p.primaryPoints ?? 0,
        secondary: p.secondaryPoints ?? 0,
        bonus: p.bonusPoints ?? 0,
      }))
      .sort((a, b) => a.t - b.t);
    const last = rows[rows.length - 1];
    if (!last || rows.length < 2) return null;

    const v7 = fitVelocity(rows, 7 * DAY);
    const v30 = fitVelocity(rows, 30 * DAY);
    const chosen = trendWindow === 7 ? (v7 ?? v30) : (v30 ?? v7);
    const usedWindow: TrendWindow | null = !chosen ? null : chosen === v7 ? 7 : 30;

    const quarterEnd = nextQuarterStart(new Date(last.t));
    const daysToQuarterEnd = (quarterEnd.getTime() - last.t) / DAY;
    const gain = (days: number) => (chosen ? Math.max(0, chosen.perDay * days) : null);

    return {
      rows, last, v7, v30, chosen, usedWindow, quarterEnd, daysToQuarterEnd,
      gain30: gain(30),
      gainQuarter: gain(daysToQuarterEnd),
    };
  }, [data, trendWindow]);

  // What the wallet's own history says a free pack costs in points
  const observedPackCost =
    pointsUsed && freePacksRedeemed && freePacksRedeemed > 0 ? pointsUsed / freePacksRedeemed : null;

  if (isLoading) return <Skeleton kind="chart" />;

  if (error || !model) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 text-center px-4">
        Not enough snapshots for a chart yet. Points are recorded every few hours once a wallet is
        active.
      </div>
    );
  }

  const { chosen, usedWindow } = model;
  const quarterLabel = model.quarterEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  const packs = (points: number | null) => (points === null ? null : Math.floor(points / packCost));

  return (
    <div className="space-y-4">
      {/* Pace and what it buys */}
      <div className="card-inset p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-gray-500">Pace</dt>
              <dd className="num font-semibold text-gray-50">
                {chosen ? `${chosen.perDay >= 0 ? "+" : ""}${compact(chosen.perDay)} pts / day` : "—"}
              </dd>
              <dd className="text-xs text-gray-500">
                {chosen ? `last ${usedWindow}d, ${chosen.samples} snapshots` : "needs snapshots 6h+ apart"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Next 30 days</dt>
              <dd className="num font-semibold text-gray-50">
                {model.gain30 !== null ? `+${compact(model.gain30)} pts` : "—"}
              </dd>
              <dd className="text-xs text-gray-400 num">
                {packs(model.gain30) !== null ? `≈ ${packs(model.gain30)!.toLocaleString()} free packs` : ""}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">By quarter end</dt>
              <dd className="num font-semibold text-gray-50">
                {model.gainQuarter !== null ? `+${compact(model.gainQuarter)} pts` : "—"}
              </dd>
              <dd className="text-xs text-gray-400 num">
                {packs(model.gainQuarter) !== null ? `≈ ${packs(model.gainQuarter)!.toLocaleString()} free packs` : ""}
                <span className="text-gray-500"> · {quarterLabel}, {Math.ceil(model.daysToQuarterEnd)} days</span>
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Days per pack</dt>
              <dd className="num font-semibold text-gray-50">
                {chosen && chosen.perDay > 0 ? (packCost / chosen.perDay).toFixed(1) : "—"}
              </dd>
              <dd className="text-xs text-gray-500">at the current pace</dd>
            </div>
          </dl>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            Pace over
            <div className="seg" role="group" aria-label="Trend window">
              <button
                className={`seg-btn ${usedWindow === 7 ? "seg-btn-on" : ""}`}
                onClick={() => setTrendWindow(7)}
                disabled={!model.v7}
                title={model.v7 ? undefined : "Not enough snapshots in the last 7 days"}
              >
                7d
              </button>
              <button
                className={`seg-btn ${usedWindow === 30 ? "seg-btn-on" : ""}`}
                onClick={() => setTrendWindow(30)}
                disabled={!model.v30}
                title={model.v30 ? undefined : "Not enough snapshots in the last 30 days"}
              >
                30d
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          <label className="inline-flex items-center gap-2">
            Points per free pack
            <input
              type="number"
              min={1}
              step={1000}
              value={packCost}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v > 0) updatePackCost(v);
              }}
              className="input py-0.5 px-2 w-28 text-xs num"
              aria-label="Points per free pack"
            />
          </label>
          {observedPackCost !== null && (
            <button
              className="text-blue-400 hover:text-blue-300"
              onClick={() => updatePackCost(Math.round(observedPackCost))}
              title="Points used divided by free packs redeemed, from this wallet's imported spin history"
            >
              This wallet's history suggests ~{Math.round(observedPackCost).toLocaleString()} per pack. Use it.
            </button>
          )}
          <span>Projections extend the recent pace in a straight line; they don't know about future spins.</span>
        </div>
      </div>

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={model.rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2739" />
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(v: number) => dateShort(v)}
              stroke="#5b6982"
              tick={{ fill: "#8b98b0", fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              minTickGap={40}
            />
            <YAxis
              stroke="#5b6982"
              tick={{ fill: "#8b98b0", fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => compact(v)}
              width={56}
            />
            <Tooltip
              cursor={{ stroke: "#8b98b0", strokeDasharray: "3 3" }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as (typeof model.rows)[number] | undefined;
                if (!active || !row) return null;
                return (
                  <div className="rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-xs shadow-lg">
                    <div className="text-gray-400">{dateLong(row.t)}</div>
                    <ul className="mt-1 space-y-0.5 num">
                      <li className="text-gray-100">Total {row.total.toLocaleString()}</li>
                      <li className="text-gray-300">Primary {row.primary.toLocaleString()}</li>
                      <li className="text-gray-300">Secondary {row.secondary.toLocaleString()}</li>
                      <li className="text-gray-300">Bonus {row.bonus.toLocaleString()}</li>
                    </ul>
                  </div>
                );
              }}
            />
            <Legend />
            <Line type="monotone" dataKey="total" stroke="#f0b84a" strokeWidth={2} dot={false} name="Total" isAnimationActive={false} />
            <Line type="monotone" dataKey="primary" stroke="#a394ff" strokeWidth={2} dot={false} name="Primary" isAnimationActive={false} />
            <Line type="monotone" dataKey="secondary" stroke="#5cd4f0" strokeWidth={2} dot={false} name="Secondary" isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
